import {
  UI_COLOR_ACCENT_ORANGE,
  UI_COLOR_BORDER_LIGHT,
  UI_COLOR_DANGER_SOFT,
  UI_COLOR_DANGER_TEXT,
  UI_COLOR_PRIMARY,
  UI_COLOR_PRIMARY_SOFT,
  UI_COLOR_RBAC_STATUS_ACTIVE_BG,
  UI_COLOR_RBAC_STATUS_ACTIVE_TEXT,
  UI_COLOR_RBAC_STATUS_INACTIVE_BG,
  UI_COLOR_RBAC_STATUS_INACTIVE_TEXT,
  UI_COLOR_SECONDARY,
  UI_COLOR_SUCCESS_SOFT,
  UI_COLOR_SUCCESS_TEXT,
  UI_COLOR_SURFACE_INFO,
  UI_COLOR_TEXT_HEADING,
  UI_COLOR_TEXT_MUTED_LIGHT,
  UI_COLOR_TEXT_SUBTITLE,
  UI_COLOR_TEXT_WARM,
  UI_COLOR_WHITE
} from '../../../../constants/uiColors'

export const PROFILE_SECURITY_CODE_LENGTH = 6
export const PROFILE_SECURITY_DEFAULT_EXPIRES_MINUTES = 10

export const PROFILE_SECURITY_EMPTY_PASSWORD_FORM = {
  password: '',
  passwordConfirmation: ''
}

export const PROFILE_SECURITY_EMPTY_RECONFIGURE_FORM = {
  currentPassword: '',
  code: ''
}

export const PROFILE_SECURITY_PASSWORD_CHANGE_ERRORS = {
  password_confirmation_mismatch: 'Las contraseñas no coinciden.',
  invalid_reset_code: 'Código inválido. Verifique el de su app o el correo más reciente.',
  reset_code_expired: 'El código expiró. Solicita uno nuevo.',
  reset_code_locked: 'Demasiados intentos. Espera unos minutos e inténtalo de nuevo.',
  invalid_reset_token: 'La verificación expiró. Vuelve a solicitar o ingresar un código.',
  password_too_long: 'La contraseña debe tener máximo 15 caracteres.'
}

export const PROFILE_SECURITY_RECONFIGURE_ERROR_MESSAGES = {
  current_password_invalid: 'La contraseña actual no es correcta.',
  invalid_mfa_code: 'Código inválido. Use el de su app actual o un código de recuperación.',
  mfa_not_enabled: 'Active la app autenticadora antes de reconfigurarla.',
  mfa_required: 'No puede quitar la app autenticadora si es su único método de verificación.'
}

export const PROFILE_SECURITY_FIELD_SX = {
  '& .MuiInputLabel-root': {
    fontSize: 14,
    color: UI_COLOR_TEXT_SUBTITLE
  },
  '& .MuiInputBase-input': {
    fontSize: 15,
    lineHeight: 1.45
  },
  '& .MuiOutlinedInput-root': {
    bgcolor: UI_COLOR_WHITE,
    borderRadius: '12px',
    minHeight: 48,
    '& fieldset': {
      borderColor: UI_COLOR_BORDER_LIGHT
    },
    '&:hover fieldset': {
      borderColor: UI_COLOR_PRIMARY
    },
    '&.Mui-focused fieldset': {
      borderColor: UI_COLOR_PRIMARY
    }
  }
}

export const PROFILE_SECURITY_BASE_CHIP_SX = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 0.5,
  minWidth: 82,
  padding: '4px 10px',
  borderRadius: '999px',
  boxSizing: 'border-box',
  fontSize: '11px',
  fontWeight: 700,
  lineHeight: 1.2,
  textTransform: 'uppercase',
  whiteSpace: 'nowrap'
}

export const PROFILE_SECURITY_STATUS_CHIP_SX = {
  active: {
    ...PROFILE_SECURITY_BASE_CHIP_SX,
    backgroundColor: UI_COLOR_RBAC_STATUS_ACTIVE_BG,
    color: UI_COLOR_RBAC_STATUS_ACTIVE_TEXT
  },
  inactive: {
    ...PROFILE_SECURITY_BASE_CHIP_SX,
    backgroundColor: UI_COLOR_RBAC_STATUS_INACTIVE_BG,
    color: UI_COLOR_RBAC_STATUS_INACTIVE_TEXT
  },
  pending: {
    ...PROFILE_SECURITY_BASE_CHIP_SX,
    backgroundColor: '#FFF4E5',
    color: '#B26A00'
  },
  available: {
    ...PROFILE_SECURITY_BASE_CHIP_SX,
    backgroundColor: UI_COLOR_PRIMARY_SOFT,
    color: UI_COLOR_PRIMARY
  },
  optional: {
    ...PROFILE_SECURITY_BASE_CHIP_SX,
    backgroundColor: '#FDECEB',
    color: '#E2583A'
  }
}

export const PROFILE_SECURITY_REQUIREMENT_CHIP_SX = {
  fulfilled: {
    ...PROFILE_SECURITY_BASE_CHIP_SX,
    minWidth: 'auto',
    gap: 0.6,
    backgroundColor: UI_COLOR_SUCCESS_SOFT,
    color: UI_COLOR_SUCCESS_TEXT
  },
  unfulfilled: {
    ...PROFILE_SECURITY_BASE_CHIP_SX,
    minWidth: 'auto',
    gap: 0.6,
    backgroundColor: UI_COLOR_DANGER_SOFT,
    color: UI_COLOR_DANGER_TEXT
  }
}

export const PROFILE_SECURITY_LOADING_BOX_SX = {
  minHeight: 320,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  border: `1px solid ${UI_COLOR_BORDER_LIGHT}`,
  borderRadius: '18px',
  bgcolor: UI_COLOR_WHITE,
  px: 3,
  py: 5
}

export const PROFILE_SECURITY_GUIDANCE_NOTE_PROPS = {
  accentColor: UI_COLOR_SECONDARY,
  backgroundColor: UI_COLOR_SURFACE_INFO,
  textColor: UI_COLOR_TEXT_WARM,
  sx: { mb: 2, border: 0, borderLeft: `4px solid ${UI_COLOR_SECONDARY}`, borderRadius: '6px' }
}

export const PROFILE_SECURITY_MODAL_GUIDANCE_PROPS = {
  accentColor: UI_COLOR_SECONDARY,
  backgroundColor: UI_COLOR_SURFACE_INFO,
  textColor: UI_COLOR_TEXT_WARM,
  singleLine: true,
  sx: {
    border: 0,
    borderLeft: `4px solid ${UI_COLOR_SECONDARY}`,
    borderRadius: '6px',
    px: { xs: 1.5, sm: 1.75 },
    py: 1.25
  },
  headerSx: { alignItems: 'flex-start', flexWrap: 'nowrap', columnGap: 1 },
  iconSx: {
    mt: 0.15,
    mr: 0,
    ml: 0,
    '& svg': {
      fontSize: 18
    }
  },
  messageSx: {
    color: UI_COLOR_TEXT_WARM,
    fontSize: 13,
    fontWeight: 400,
    lineHeight: 1.45
  }
}

export const PROFILE_SECURITY_WARNING_NOTE_PROPS = {
  accentColor: '#C4611C',
  backgroundColor: '#FFF4E5',
  textColor: '#000000',
  singleLine: true,
  sx: {
    border: 0,
    borderLeft: '4px solid #C4611C',
    borderRadius: '6px',
    px: { xs: 1.5, sm: 1.75 },
    py: 1.25
  },
  headerSx: { alignItems: 'flex-start', flexWrap: 'nowrap', columnGap: 1 },
  iconSx: {
    mt: 0.15,
    mr: 0,
    ml: 0,
    '& svg': {
      fontSize: 18
    }
  },
  messageSx: {
    color: '#000000',
    fontSize: 13,
    fontWeight: 400,
    lineHeight: 1.45
  }
}

export const PROFILE_SECURITY_BACKUP_FILE_NAME = 'codigos-recuperacion.txt'
export const PROFILE_SECURITY_BACKUP_FILE_LINES = {
  title: 'Códigos de recuperación - Torre de Control',
  safeMessage: 'Guárdelos en un lugar seguro. Cada código es de un solo uso.'
}

export const PROFILE_SECURITY_COPY = {
  loading: 'Cargando...',
  loadError: 'No fue posible cargar seguridad.',
  missingEmail: 'No encontramos un correo asociado a su cuenta.',
  invalidVerificationCode: 'Ingrese un código válido de 6 dígitos.',
  passwordCodeSent: 'Le enviamos un código al correo registrado.',
  passwordCodeVerified: 'Código verificado. Ya puede cambiar su contraseña.',
  passwordChanged: 'Contraseña actualizada.',
  passwordMismatch: 'Las contraseñas no coinciden.',
  passwordRulesInvalid: 'La nueva contraseña no cumple con las reglas mínimas.',
  setupMfaError: 'No fue posible iniciar la app autenticadora.',
  mfaInvalidCode: 'Código inválido.',
  mfaEnabled: 'App autenticadora activada. El código por correo sigue siendo el método principal.',
  mfaReconfigured: 'App autenticadora reconfigurada. Guarde los códigos nuevos; la app anterior ya no funciona.',
  mfaDisabled: 'App autenticadora desactivada. El código por correo sigue activo.',
  reconfigureValidation: 'Ingrese su contraseña y el código actual o de recuperación.',
  reconfigureFallback: 'No fue posible iniciar la reconfiguración.',
  backupCodesGenerated: 'Nuevos códigos generados. Guárdelos ahora.',
  backupCodesRegenerateError: 'No fue posible regenerar los códigos.',
  backupCodesCopied: 'Códigos copiados al portapapeles.',
  backupCodesCopyError: 'No fue posible copiar los códigos.',
  backupCodesDownloaded: 'Archivo TXT descargado.',
  backupCodesEmpty: 'No hay códigos nuevos para mostrar. Active la app autenticadora para generarlos.',
  statusPassword: 'Contraseña',
  statusPasswordValue: 'Protegida',
  statusEmail: 'Correo',
  statusEmailActive: 'Activo',
  statusEmailPending: 'Pendiente',
  statusAuthenticator: 'App de autenticación',
  statusAuthenticatorActive: 'Activo',
  statusAuthenticatorOptional: 'Opcional',
  statusRecovery: 'Recuperación',
  statusRecoveryPending: 'Pendiente',
  statusRecoverySaveNow: 'Guardar ahora',
  passwordCardEyebrow: 'Acceso',
  passwordCardTitle: 'Cambiar contraseña',
  passwordCardDescription:
    'Al apretar «Editar», le enviaremos un código a su correo para poder restablecer su contraseña. Si tiene una app autenticadora como doble factor de autenticación, también puede usar ese código o uno de recuperación.',
  passwordCardAction: 'Editar',
  emailCardEyebrow: 'Principal',
  emailCardTitle: 'Código por correo',
  authenticatorCardEyebrowOptional: 'Opcional',
  authenticatorCardEyebrowActive: 'Activo',
  authenticatorCardTitle: 'Configurar App autenticadora',
  authenticatorCardDescriptionOptional:
    'Si desea aumentar el nivel de seguridad, puede activar el doble factor de autenticación a través de un App de autenticación.',
  authenticatorCardActionActivate: 'Activar app',
  authenticatorCardActionReconfigure: 'Reconfigurar',
  authenticatorCardActionDisable: 'Desactivar',
  recoveryCardEyebrow: 'Recuperación',
  recoveryCardTitle: 'Códigos de recuperación',
  recoveryCardDescriptionPending:
    'Los códigos de recuperación son códigos que se otorgan al activar el doble factor de autenticación por aplicación. Le ayudarán a configurar de nuevo el factor por aplicación en otro dispositivo o a recuperar la contraseña.',
  recoveryCardDescriptionSaveNow:
    'Guárdelos ahora. Solo se muestran una vez; si cierra esta vista o los pierde, tendrá que regenerar un set nuevo.',
  recoveryCardAction: 'Regenerar',
  mfaSetupTitle: 'Activar autenticación en dos pasos',
  mfaSetupSubtitle:
    'Descargue una app de autenticación como Google Authenticator, Microsoft Authenticator o 1Password en su celular.',
  mfaSetupGuidance:
    'Abra su app de autenticación en su celular, escanee el código y escriba el número de 6 dígitos.',
  mfaSetupQrLabel: 'Código QR',
  mfaSetupCodeLabel: 'Código de verificación'
}

export const PROFILE_SECURITY_PASSWORD_REQUIREMENT_LABELS = [
  { key: 'length', label: 'Entre 8 y 15 caracteres' },
  { key: 'lower', label: 'Al menos 1 letra minúscula (a-z)' },
  { key: 'number', label: 'Al menos 1 número (0-9)' },
  { key: 'special', label: 'Al menos 1 carácter especial' }
]

export const PROFILE_SECURITY_BACKUP_HINT_ICON_COLOR = UI_COLOR_TEXT_MUTED_LIGHT
export const PROFILE_SECURITY_BACKUP_CODE_ICON_COLOR = UI_COLOR_PRIMARY
export const PROFILE_SECURITY_HEADING_COLOR = UI_COLOR_TEXT_HEADING
export const PROFILE_SECURITY_SUBTITLE_COLOR = UI_COLOR_TEXT_SUBTITLE
export const PROFILE_SECURITY_BORDER_COLOR = UI_COLOR_BORDER_LIGHT
export const PROFILE_SECURITY_PRIMARY_SOFT = UI_COLOR_PRIMARY_SOFT
export const PROFILE_SECURITY_WHITE = UI_COLOR_WHITE
export const PROFILE_SECURITY_ACCENT_ORANGE = UI_COLOR_ACCENT_ORANGE
